import { useEffect, useState } from 'react'

// One call surface per tab.
//
// The server claim identifies a TAB (clientId), so two surfaces in the same tab — the floating call, the
// AI-coach surface, a dashboard embed — would both be granted and both connect. This slot makes the first
// surface that asks the only one that connects; the others show a note (there is nothing for the user to
// confirm inside their own tab).

let slotHolder: string | null = null
let nextSurfaceId = 0
const slotWatchers = new Set<() => void>()

// A surface that unmounts hands the call to the next surface in this tab surprisingly often (dashboard
// embed → floating call, or a takeover that navigates into the game). Wait this long before giving the
// claim back to the server, so a same-tab handover never frees the slot for another tab to grab.
export const TAB_SLOT_RELEASE_GRACE_MS = 2000

/** True while no surface in this tab holds the call. */
export const isTabSlotFree = (): boolean => slotHolder === null

const takeSlot = (surfaceId: string): boolean => {
  if (slotHolder === null) slotHolder = surfaceId
  return slotHolder === surfaceId
}

const releaseSlot = (surfaceId: string): void => {
  if (slotHolder !== surfaceId) return
  slotHolder = null
  // Let a surface that was waiting (e.g. a dashboard embed behind the floating call) take over.
  slotWatchers.forEach((notify) => notify())
}

/**
 * Claims this tab's single call slot while `wants` is true.
 * Returns whether THIS surface holds it; a surface that doesn't must not connect.
 */
export const useWherebyTabSlot = (wants: boolean): boolean => {
  const [surfaceId] = useState(() => {
    nextSurfaceId += 1
    return `surface-${nextSurfaceId}`
  })
  const [hasSlot, setHasSlot] = useState(false)

  useEffect(() => {
    if (!wants) return
    const evaluate = () => setHasSlot(takeSlot(surfaceId))
    evaluate()
    slotWatchers.add(evaluate)
    return () => {
      slotWatchers.delete(evaluate)
      releaseSlot(surfaceId)
      setHasSlot(false)
    }
  }, [wants, surfaceId])

  return hasSlot
}
